import SearchBarResultCategory from "enums/searchResultCategory";

/**
 * Truncates the text to a specified maximum length and appends an ellipsis if truncated.
 * @param {string} text - The text to truncate.
 * @param {number} maxLength - The maximum length of the truncated text.
 * @returns {string} The truncated text with an ellipsis if truncated.
 */
export const truncateWithEllipsis = (text = '', maxLength = 90) => {
  if (!text || text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength).trim()}...`;
};

/**
 * CSS classes used for styling highlighted and regular text.
 */
const CSS_CLASSES = {
  regular: 'nhs-n1-c-search-bar-results__item__container__title--regular',
  bold: 'nhs-n1-c-search-bar-results__item__container__title--bold',
};

/**
 * Escapes special characters in a string for use in a regular expression.
 * @param {string} text - The text to escape.
 * @returns {string} The escaped text.
 */
const escapeRegex = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Returns the title with all occurrences of searchText highlighted by wrapping them in a <span> with bold styling.
 * @param {string} title - The text to search within.
 * @param {string} searchText - The text to highlight.
 * @returns {string} The HTML string with highlighted text.
 */
const getHighlightedText = (title = '', searchText = '') => {
  if (!title || searchText.trim().length < 2) {
    return title;
  }

  const regex = new RegExp(escapeRegex(searchText), 'gi');

  return title.replace(regex, (match) => `<span class="${CSS_CLASSES.bold}">${match}</span>`);
};

/**
 * Returns the title with all occurrences of searchText highlighted and wrapped in a <div> with regular styling.
 * @param {string} title - The text to search within.
 * @param {string} searchText - The text to highlight.
 * @returns {string} The HTML string with highlighted text wrapped in a <div>.
 */
export const getHighlightedTitleHtml = (title = '', searchText = '') => {
  const highlighted = getHighlightedText(title, searchText);
  return `<div class="${CSS_CLASSES.regular}">${highlighted}</div>`;
};

/**
 * Normalizes the category type to a lowercase string.
 * @param {string|number} categoryType - The category type to normalize.
 * @returns {string} The normalized category type.
 */
const normalizeCategoryType = (categoryType) => String(categoryType).toLowerCase();

/**
 * Checks if a search result item matches the specified category type.
 * @param {Object} item - The search result item.
 * @param {string} categoryType - The category type to match against.
 * @returns {boolean} True if the item matches the category type, false otherwise.
 */
const isMatchingCategory = (item, categoryType) => {
  const itemCategory = item?.SearchBarResultCategoryType;
  return itemCategory && normalizeCategoryType(itemCategory) === categoryType;
};

/**
 * Filters the search results by the specified category type.
 * @param {Array} results - The array of search results to filter.
 * @param {string|number} categoryType - The category type to filter by.
 * @returns {Array} The filtered array of search results.
 */
export const filterSearchResultsByCategoryType = (
  results = [], categoryType = SearchBarResultCategory.AllResults.id) => {

  if (!results.length || normalizeCategoryType(categoryType) === SearchBarResultCategory.AllResults.id) {
    return results;
  }

  const normalizedCategory = normalizeCategoryType(categoryType);
  return results.filter((item) => isMatchingCategory(item, normalizedCategory));
};

/**
 * Mapping of category type identifiers to their corresponding enum names.
 */
const CATEGORY_MAP = new Map([
  [SearchBarResultCategory.Location.id, SearchBarResultCategory.Location.toString()],
  [SearchBarResultCategory.Community.id, SearchBarResultCategory.Community.toString()],
  [SearchBarResultCategory.Builder.id, SearchBarResultCategory.Builder.toString()],
  [SearchBarResultCategory.Article.id, SearchBarResultCategory.Article.toString()],
]);

/**
 * Increments the count for a specific category type.
 * @param {Object} counts - The counts object to update.
 * @param {number|string} categoryType - The category type to increment.
 */
const incrementCount = (counts, categoryType) => {
  const key = CATEGORY_MAP.get(String(categoryType));
  if (key) { counts[key] += 1; }
};

/**
 * Initializes the counts object with zero values.
 * @param {number} resultsLength - The total number of results.
 * @returns {Object} The initialized counts object.
 */
const initializeCounts = (resultsLength) => ({
  total: resultsLength,
  locations: 0,
  communities: 0,
  builders: 0,
  articles: 0,
});

/**
 * Computes the counts of search results by their category types.
 * @param {Array} results - The array of search results to analyze.
 * @returns {Object} An object containing the total count and counts for each category type.
 */
export const searchResultsCountsByCategoryType = (results = []) => {
  const counts = initializeCounts(results.length);

  results.forEach(({ SearchBarResultCategoryType }) => {
    incrementCount(counts, SearchBarResultCategoryType);
  });

  return counts;
};

/**
 * Determines the visibility of left and right tab scroll buttons based on the scroll position.
 * @param {number} scrollLeft - The current horizontal scroll position.
 * @param {number} scrollWidth - The total scrollable width of the container.
 * @param {number} clientWidth - The visible width of the container.
 * @returns {Object} An object containing the visibility status of the left and right scroll buttons.
 */
export const getTabScrollButtonVisibility = (scrollLeft, scrollWidth, clientWidth) => {
  if (scrollWidth <= clientWidth) {
    return { leftHidden: true, rightHidden: true }; // No scrolling needed
  }

  const atExtremeLeft = scrollLeft <= 0;
  const atExtremeRight = Math.ceil(scrollLeft + clientWidth) >= scrollWidth;

  return {
    leftHidden: atExtremeLeft,
    rightHidden: atExtremeRight
  };
}

/**
 * Determines if the search results container should open to the left based on available space.
 * @param {HTMLElement} searchBarContainer - The search bar container element.
 * @param {HTMLElement} resultsDropdown - The search results dropdown element.
 * @returns {boolean} True if the container should open to the left, false otherwise.
 */
const isSearchResultsOpenLeftDirection = (searchBarContainer, resultsDropdown) => {
  const { left } = searchBarContainer.getBoundingClientRect();
  const viewportWidth = document.documentElement.clientWidth;

  return viewportWidth - left < resultsDropdown.offsetWidth;
};

/**
 * Repositions the results container when it overflows the viewport horizontally.
 * Mirrors the legacy search bar behavior by shifting left/right via translateX.
 * @param {Object} context - Search bar result component context.
 * @param {Object} fastdom - FastDOM instance for batching DOM reads/writes.
 */
export const handleResultsContainerPlacement = (context, fastdom) => {
  const { resultContainer } = context;
  const searchBarContainer = resultContainer?.closest('[data-search-bar-container]');
  if (!resultContainer || !searchBarContainer || !fastdom) {
    return;
  }

  const openLeftCssClass = 'nhs-n1-c-search-bar-with-results-wrapper__results__left';

  fastdom.measure(() => {
    const isOpenLeft = isSearchResultsOpenLeftDirection(searchBarContainer, resultContainer);

    fastdom.mutate(() => {
      resultContainer.classList.toggle(
        openLeftCssClass,
        isOpenLeft
      );
    });
  });
};

/**
 * Safely parses a JSON string into an object.
 * Returns null if parsing fails.
 * @param {string} jsonString - The JSON string to parse.
 * @returns {Object|null} The parsed object, or null if parsing fails.
 */
export const parseJSON = (jsonString) => {
  try {
    return JSON.parse(jsonString);
  } catch {
    return null;
  }
};
